import React, {
  useReducer,
  createContext,
  useState,
  useEffect,
  useRef
} from "react";
import { playByPlayReducer } from "./playByPlayReducer";
import { transformFormToData, transformDataToForm } from "./transformations";
import { useNotify, useUpdate, useCreate } from "react-admin";
import _ from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import { isDisplayable, isLgLocationById, lgLocationIdParts } from "util/misc";

const useStyles = makeStyles({
  input: {
    marginRight: "10px",
    marginBottom: "10px"
  }
});

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const PlayByPlayContext = createContext();

export const PlayByPlayContextProvider = ({ game, children }) => {
  const [state, dispatch] = useReducer(playByPlayReducer, {});
  const { plays, stats, pristinePlays, pristineStats, shouldSave } = state;
  const [shouldUpdateData, setShouldUpdateData] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const notify = useNotify();
  const [update] = useUpdate("games");
  const [create] = useCreate("comboLocations");
  const styles = useStyles();
  const [isPristine, setIsPristine] = useState(true);
  const prevState = usePrevious(state);
  const prevPlays = _.get(prevState, "plays");
  const prevStats = _.get(prevState, "stats");

  // after the game has been retrieved, set up the state based on the game data
  useEffect(() => {
    const gameDataExists = isDisplayable(_.get(game, "playsBlob"));
    if (!gameDataExists || !shouldUpdateData) return;
    const formData = transformDataToForm(game);
    dispatch({
      type: "setState",
      payload: {
        plays: formData.plays,
        stats: formData.stats,
        pristinePlays: formData.plays,
        pristineStats: formData.stats,
        game: game,
        gameStatus: _.get(game, "gameStatus"),
        shouldSave: false,
        data: {}
      }
    });
    setIsLoading(false);
    setShouldUpdateData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game]);

  useEffect(() => {
    // if the previous plays / stats dont exist, that means that the game is
    // still getting retrieved
    if (!isDisplayable(prevPlays) && !isDisplayable(prevStats)) return;
    if (_.isEqual(plays, pristinePlays) && _.isEqual(stats, pristineStats)) {
      setIsPristine(true);
    } else {
      setIsPristine(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plays, stats]);

  useEffect(() => {
    if (!shouldSave) return;
    const location = _.get(state, "data.location");

    if (!isLgLocationById(location)) {
      onSave(_.get(location, "id", location));
    } else {
      const { name, stableId } = lgLocationIdParts(location);
      const locationData = { name, stableId };
      create(
        { payload: { data: locationData } },
        {
          onSuccess: ({ data }) => {
            onSave(_.get(data, "id"));
          },
          onFailure: ({ error }) => {
            notify(error.message, "error");
            setIsLoading(false);
          }
        }
      );
    }

    setIsPristine(true);
    dispatch({
      type: "setState",
      payload: {
        shouldSave: false,
        pristinePlays: plays,
        pristineStats: stats
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSave]);

  const onSave = (location = null) => {
    const saveGame = async (gameId) => {
      setIsLoading(true);

      const playsBlob = transformFormToData({
        plays: state.plays,
        stats: state.stats,
        gameStatus: state.gameStatus
      });

      const data = {
        playsBlob,
        ...state.data,
        ...(location && { location })
      };

      update(
        {
          payload: {
            data,
            id: gameId
          }
        },
        {
          onSuccess: ({ data }) => {
            setIsLoading(false);
            notify("Game Updated");
          },
          onFailure: ({ error }) => {
            notify(error.message, "error");
            setIsLoading(false);
          }
        }
      );
    };
    const gameId = _.get(game, "id");
    saveGame(gameId);
  };

  return (
    <PlayByPlayContext.Provider
      value={{
        usePlayByPlay: [state, dispatch],
        isLoading,
        isPristine,
        styles,
        setShouldUpdateData
      }}
    >
      {children}
    </PlayByPlayContext.Provider>
  );
};
